import React, { useEffect, useState } from "react";
import { 
  Modal, 
  Form, 
  Input, 
  Checkbox, 
  Row, 
  Col, 
  DatePicker, 
  Typography, 
  Button, 
  Card, 
  Space, 
  Tag,
  Tabs,
  List,
  InputNumber,
  Select,
  message,
  Popconfirm
} from "antd";
import { 
  CoffeeOutlined, 
  CheckSquareOutlined, 
  FormatPainterOutlined, 
  FireOutlined, 
  WarningOutlined,
  ProfileOutlined,
  HomeOutlined,
  UserOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  PrinterOutlined,
  SaveOutlined,
  CloseOutlined,
  PlusOutlined,
  DeleteOutlined,
  InfoCircleOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import { FrontDeskBooking } from "../../../types";
import { frontDeskService } from "../../../services/adminService";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface RoomCheckOutInspectionModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
  booking: FrontDeskBooking | null;
}

export const RoomCheckOutInspectionModal: React.FC<RoomCheckOutInspectionModalProps> = ({
  open,
  onCancel,
  onSuccess,
  booking,
}) => {
  const [form] = Form.useForm();
  const [fines, setFines] = useState<{ id?: string; category: string; description: string; amount: number }[]>([]);
  const [loading, setLoading] = useState(false);
  const [newFine, setNewFine] = useState<{ category: string; description: string; amount: number | null }>({
    category: "other",
    description: "",
    amount: null
  });

  useEffect(() => {
    if (open && booking) {
      const initialData = booking.inspection_form_data || {};
      
      form.setFieldsValue({
        room_no: booking.room_number || "Not Allocated",
        guest_name: booking.guest_name,
        date: dayjs(),
        housekeeper: initialData.housekeeper || "",
        notes: booking.inspection_notes || "",
        final_status: booking.inspection_status === "completed" || booking.inspection_status === "cleared" ? ["cleared", "ready"] : [],
        mini_fridge_items: initialData.mini_fridge_items || [],
        mini_fridge_consumed: initialData.mini_fridge_consumed || "",
        room_inventory: initialData.room_inventory || [],
        inventory_missing: initialData.inventory_missing || "",
        linen_check: initialData.linen_check || [],
        linen_missing: initialData.linen_missing || "",
        bathroom_check: initialData.bathroom_check || [],
        bathroom_remarks: initialData.bathroom_remarks || "",
        condition_check: initialData.condition_check || [],
      });
      fetchFines();
    } else {
      form.resetFields();
      setFines([]);
    }
  }, [open, booking, form]);

  const fetchFines = async () => {
    if (!booking) return;
    try {
      const res: any = await frontDeskService.getBookingFines(booking.id);
      if (res.data) {
        setFines(res.data);
      }
    } catch (err) {
      console.error("Failed to fetch fines:", err);
    }
  };

  const handleAddFine = () => {
    if (!newFine.description || !newFine.amount) {
      message.error("Please enter both description and amount");
      return;
    }
    setFines([...fines, { ...newFine, amount: newFine.amount }]);
    setNewFine({ category: "other", description: "", amount: null });
  };

  const handleRemoveFine = (index: number) => {
    const newFines = [...fines];
    newFines.splice(index, 1);
    setFines(newFines);
  };

  const handlePrint = async () => {
    let currentInspNumber = booking?.inspection_number;
    
    if (!currentInspNumber && booking) {
      // Auto-save the inspection to get the ID before printing
      try {
        setLoading(true);
        const values = await form.validateFields();
        const status = values.final_status?.includes("cleared") ? "cleared" : "completed";
        
        const response: any = await frontDeskService.saveBookingFines(booking.id, {
          fines: fines.map(f => ({ category: f.category, description: f.description, amount: Number(f.amount) })),
          notes: values.notes,
          status: status,
          data: values
        });
        
        currentInspNumber = response.data?.inspectionNumber;
        if (booking) {
          booking.inspection_number = currentInspNumber;
        }
        if (onSuccess) onSuccess();
        message.success("Inspection auto-saved for printing");
      } catch (err) {
        message.error("Failed to save inspection for printing");
        setLoading(false);
        return;
      }
    }

    if (!currentInspNumber || !booking) {
      message.error("Unable to generate inspection form.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await frontDeskService.getInspectionForm(booking.id);
      const url = window.URL.createObjectURL(new Blob([response], { type: 'application/pdf' }));
      window.open(url, "_blank");
    } catch (err) {
      message.error("Failed to load inspection form");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!booking) return;
    setLoading(true);
    try {
      const values = await form.validateFields();
      const status = values.final_status?.includes("cleared") ? "cleared" : "completed";
      
      const response: any = await frontDeskService.saveBookingFines(booking.id, {
        fines: fines.map(f => ({ category: f.category, description: f.description, amount: Number(f.amount) })),
        notes: values.notes,
        status: status,
        data: values
      });
      
      if (response.data?.inspectionNumber) {
        booking.inspection_number = response.data.inspectionNumber;
      }
      
      message.success("Inspection saved successfully");
      if (onSuccess) onSuccess();
      // Optionally keep it open so they can print, or close it. Let's keep it open for a better flow
      // onCancel();
    } catch (err) {
      console.error(err);
      message.error("Failed to save inspection");
    } finally {
      setLoading(false);
    }
  };

  const totalFines = fines.reduce((sum, f) => sum + Number(f.amount), 0);

  const tabsItems = [
    {
      key: "1",
      label: <span><InfoCircleOutlined /> General Info</span>,
      children: (
        <Card size="small" bordered={false}>
          <Row gutter={16}>
            {booking?.inspection_number && (
              <Col span={24}>
                <Form.Item label={<Space><InfoCircleOutlined /> Inspection ID</Space>} style={{ marginBottom: 12 }}>
                  <Text strong type="danger" style={{ fontSize: '16px' }}>{booking.inspection_number}</Text>
                </Form.Item>
              </Col>
            )}
            <Col span={12}>
              <Form.Item label={<Space><HomeOutlined /> Room No</Space>} name="room_no" style={{ marginBottom: 12 }}>
                <Input readOnly bordered={false} style={{ fontWeight: 'bold', fontSize: '16px' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={<Space><UserOutlined /> Guest Name</Space>} name="guest_name" style={{ marginBottom: 12 }}>
                <Input readOnly bordered={false} style={{ fontWeight: 'bold', fontSize: '16px' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={<Space><CalendarOutlined /> Date</Space>} name="date" style={{ marginBottom: 12 }}>
                <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" bordered={false} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={<Space><UserOutlined /> Housekeeper Name</Space>} name="housekeeper" style={{ marginBottom: 12 }}>
                <Input placeholder="Enter housekeeper name" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Inspection Notes" name="notes" style={{ marginBottom: 12 }}>
                <TextArea rows={3} placeholder="Overall inspection notes..." />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Final Status" name="final_status" style={{ marginBottom: 0 }}>
                <Checkbox.Group style={{ width: '100%' }}>
                  <Row gutter={[0, 8]}>
                    <Col span={12}><Checkbox value="cleared">Room Cleared</Checkbox></Col>
                    <Col span={12}><Checkbox value="ready">Ready for Next Guest</Checkbox></Col>
                  </Row>
                </Checkbox.Group>
              </Form.Item>
            </Col>
          </Row>
        </Card>
      )
    },
    {
      key: "2",
      label: <span><CoffeeOutlined /> Mini Fridge</span>,
      children: (
        <Card size="small" bordered={false}>
          <Form.Item name="mini_fridge_items" style={{ marginBottom: 16 }}>
            <Checkbox.Group style={{ width: '100%' }}>
              <Row gutter={[0, 8]}>
                <Col span={12}><Checkbox value="water">Water Bottles</Checkbox></Col>
                <Col span={12}><Checkbox value="soft_drinks">Soft Drinks</Checkbox></Col>
                <Col span={12}><Checkbox value="beer">Beer</Checkbox></Col>
                <Col span={12}><Checkbox value="snacks">Snacks</Checkbox></Col>
                <Col span={24}><Checkbox value="other">Other Items</Checkbox></Col>
              </Row>
            </Checkbox.Group>
          </Form.Item>
          <Form.Item label="Items Consumed" name="mini_fridge_consumed" style={{ marginBottom: 12 }}>
            <Input placeholder="List consumed items" />
          </Form.Item>
        </Card>
      )
    },
    {
      key: "3",
      label: <span><CheckSquareOutlined /> Inventory</span>,
      children: (
        <Card size="small" bordered={false}>
          <Form.Item name="room_inventory" style={{ marginBottom: 16 }}>
            <Checkbox.Group style={{ width: '100%' }}>
              <Row gutter={[0, 8]}>
                <Col span={12}><Checkbox value="tv_remote">TV Remote</Checkbox></Col>
                <Col span={12}><Checkbox value="ac_remote">A/C Remote</Checkbox></Col>
                <Col span={12}><Checkbox value="room_key">Room Key</Checkbox></Col>
                <Col span={12}><Checkbox value="kettle">Electric Kettle</Checkbox></Col>
                <Col span={12}><Checkbox value="tea_cups">Tea Cups & Saucers</Checkbox></Col>
                <Col span={12}><Checkbox value="glasses">Glasses & Spoons</Checkbox></Col>
                <Col span={12}><Checkbox value="water_jug">Water Jug</Checkbox></Col>
                <Col span={12}><Checkbox value="dust_bin">Dust Bin</Checkbox></Col>
                <Col span={24}><Checkbox value="hair_dryer">Hair Dryer (if applicable)</Checkbox></Col>
              </Row>
            </Checkbox.Group>
          </Form.Item>
          <Form.Item label="Missing/Damaged Items" name="inventory_missing" style={{ marginBottom: 0 }}>
            <TextArea rows={2} placeholder="Describe any issues" />
          </Form.Item>
        </Card>
      )
    },
    {
      key: "4",
      label: <span><FormatPainterOutlined /> Linen</span>,
      children: (
        <Card size="small" bordered={false}>
          <Form.Item name="linen_check" style={{ marginBottom: 16 }}>
            <Checkbox.Group style={{ width: '100%' }}>
              <Row gutter={[0, 8]}>
                <Col span={12}><Checkbox value="bath_towels">Bath Towels</Checkbox></Col>
                <Col span={12}><Checkbox value="hand_towels">Hand Towels</Checkbox></Col>
                <Col span={12}><Checkbox value="face_towels">Face Towels</Checkbox></Col>
                <Col span={12}><Checkbox value="bed_sheets">Bed Sheets</Checkbox></Col>
                <Col span={12}><Checkbox value="pillow_covers">Pillow Covers</Checkbox></Col>
                <Col span={12}><Checkbox value="blanket">Blanket / Duvet</Checkbox></Col>
              </Row>
            </Checkbox.Group>
          </Form.Item>
          <Form.Item label="Missing/Damaged Items" name="linen_missing" style={{ marginBottom: 0 }}>
            <TextArea rows={2} placeholder="Describe any issues" />
          </Form.Item>
        </Card>
      )
    },
    {
      key: "5",
      label: <span><CheckSquareOutlined /> Bathroom</span>,
      children: (
        <Card size="small" bordered={false}>
          <Form.Item name="bathroom_check" style={{ marginBottom: 16 }}>
            <Checkbox.Group style={{ width: '100%' }}>
              <Row gutter={[0, 8]}>
                <Col span={12}><Checkbox value="wash_basin">Wash Basin</Checkbox></Col>
                <Col span={12}><Checkbox value="toilet_seat">Toilet Seat</Checkbox></Col>
                <Col span={12}><Checkbox value="shower">Shower</Checkbox></Col>
                <Col span={12}><Checkbox value="water_heater">Water Heater</Checkbox></Col>
                <Col span={12}><Checkbox value="mirror">Mirror</Checkbox></Col>
                <Col span={12}><Checkbox value="door_lock">Door Lock</Checkbox></Col>
                <Col span={24}><Checkbox value="toilet_flush">Toilet Flush Working</Checkbox></Col>
              </Row>
            </Checkbox.Group>
          </Form.Item>
          <Form.Item label="Remarks" name="bathroom_remarks" style={{ marginBottom: 0 }}>
            <TextArea rows={2} placeholder="Additional remarks" />
          </Form.Item>
        </Card>
      )
    },
    {
      key: "6",
      label: <span><HomeOutlined /> Condition</span>,
      children: (
        <Card size="small" bordered={false}>
          <Form.Item name="condition_check" style={{ marginBottom: 16 }}>
            <Checkbox.Group style={{ width: '100%' }}>
              <Row gutter={[0, 8]}>
                <Col span={12}><Checkbox value="bed">Bed & Mattress</Checkbox></Col>
                <Col span={12}><Checkbox value="wardrobe">Wardrobe</Checkbox></Col>
                <Col span={12}><Checkbox value="curtains">Curtains</Checkbox></Col>
                <Col span={12}><Checkbox value="walls">Walls & Ceiling</Checkbox></Col>
                <Col span={12}><Checkbox value="lights">Lights & Fan</Checkbox></Col>
                <Col span={12}><Checkbox value="ac">Air Conditioner</Checkbox></Col>
                <Col span={12}><Checkbox value="windows">Windows</Checkbox></Col>
                <Col span={12}><Checkbox value="door_lock">Door Lock</Checkbox></Col>
                <Col span={24}><Checkbox value="balcony">Balcony Area</Checkbox></Col>
              </Row>
            </Checkbox.Group>
          </Form.Item>
          <Form.Item label="Remarks" name="condition_remarks" style={{ marginBottom: 0 }}>
            <TextArea rows={2} placeholder="Additional remarks" />
          </Form.Item>
        </Card>
      )
    },
    {
      key: "7",
      label: <span><FireOutlined /> No Smoking</span>,
      children: (
        <Card size="small" bordered={false}>
          <Form.Item name="smoking_policy" style={{ marginBottom: 16 }}>
            <Checkbox.Group style={{ width: '100%' }}>
              <Row gutter={[0, 8]}>
                <Col span={24}><Checkbox value="no_evidence">No Smoking Evidence Found</Checkbox></Col>
                <Col span={24}><Checkbox value="smell_detected">Smoking Smell Detected</Checkbox></Col>
                <Col span={24}><Checkbox value="butts_found">Cigarette Butts Found</Checkbox></Col>
                <Col span={24}><Checkbox value="charge_applicable">Smoking Charge Applicable</Checkbox></Col>
              </Row>
            </Checkbox.Group>
          </Form.Item>
          <Form.Item label="Remarks" name="smoking_remarks" style={{ marginBottom: 0 }}>
            <TextArea rows={2} placeholder="Additional remarks" />
          </Form.Item>
        </Card>
      )
    }
  ];

  return (
    <Modal
      open={open}
      title={
        <Space>
          <ProfileOutlined style={{ color: '#1890ff' }} />
          Room Check-Out Inspection Checklist
        </Space>
      }
      onCancel={onCancel}
      width={1200}
      style={{ top: 20 }}
      footer={[
        <Button key="print" type="dashed" icon={<PrinterOutlined />} onClick={handlePrint} style={{ float: 'left' }}>
          Print Form
        </Button>,
        <Button key="cancel" icon={<CloseOutlined />} onClick={onCancel}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" icon={<SaveOutlined />} onClick={handleSubmit} loading={loading}>
          Save Inspection
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        <Row gutter={24}>
          <Col span={16}>
            <Tabs defaultActiveKey="1" items={tabsItems} />
          </Col>
          <Col span={8}>
            <Card 
              title={<Space><WarningOutlined style={{ color: '#faad14' }}/> Fine Summary Cart</Space>} 
              size="small" 
              bordered={true} 
              style={{ height: '100%', minHeight: '400px', display: 'flex', flexDirection: 'column' }}
              bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column' }}
            >
              <div style={{ flex: 1, overflowY: 'auto', marginBottom: 16 }}>
                <List
                  itemLayout="horizontal"
                  dataSource={fines}
                  locale={{ emptyText: "No fines added" }}
                  renderItem={(item, index) => (
                    <List.Item
                      actions={[
                        <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleRemoveFine(index)} />
                      ]}
                    >
                      <List.Item.Meta
                        title={<Text strong>{item.category.replace('_', ' ').toUpperCase()}</Text>}
                        description={item.description}
                      />
                      <div style={{ fontWeight: 'bold' }}>Rs. {Number(item.amount).toFixed(2)}</div>
                    </List.Item>
                  )}
                />
              </div>

              <div style={{ padding: '12px 0', borderTop: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0', marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text strong style={{ fontSize: '16px' }}>Total Fines:</Text>
                  <Text strong style={{ fontSize: '18px', color: '#f5222d' }}>Rs. {totalFines.toFixed(2)}</Text>
                </div>
              </div>

              <div>
                <Text strong>Add New Fine</Text>
                <div style={{ marginTop: 8 }}>
                  <Select 
                    value={newFine.category} 
                    onChange={(val) => setNewFine({...newFine, category: val})}
                    style={{ width: '100%', marginBottom: 8 }}
                  >
                    <Option value="mini_fridge">Mini Fridge</Option>
                    <Option value="inventory">Room Inventory</Option>
                    <Option value="linen">Linen & Towel</Option>
                    <Option value="bathroom">Bathroom</Option>
                    <Option value="condition">Room Condition</Option>
                    <Option value="smoking">Smoking Violation</Option>
                    <Option value="other">Other</Option>
                  </Select>
                  <Input 
                    placeholder="Description" 
                    value={newFine.description}
                    onChange={(e) => setNewFine({...newFine, description: e.target.value})}
                    style={{ marginBottom: 8 }}
                  />
                  <InputNumber 
                    placeholder="Amount" 
                    value={newFine.amount}
                    onChange={(val) => setNewFine({...newFine, amount: val})}
                    style={{ width: '100%', marginBottom: 8 }}
                    prefix="Rs."
                  />
                  <Button type="dashed" block icon={<PlusOutlined />} onClick={handleAddFine}>
                    Add Fine
                  </Button>
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
